import React, { useMemo } from "react";

import { useXeroBrandingThemes } from "../../hooks/useXeroBrandingThemes";
import { Select } from "../../components/ui";

import "./xero-org-select.scss";

/**
 * "Branding" picker shown beside the Xero Organisation select in the PAYMENTS
 * section of the order / sell / PX flows. Chooses which branding theme —
 * Xero's letterhead template — the invoice (order / PX) or purchase order
 * (sell) is generated with.
 *
 * The themes belong to the SELECTED organisation, so the list is re-fetched
 * whenever the org changes and a pick never carries across orgs.
 *
 * A null value means "the organisation's default", which is what every document
 * used before this picker existed; the default theme is shown as selected
 * rather than an empty box, because that IS what the document will carry.
 *
 * Hidden when the org has no themes to offer (or Xero could not be reached) —
 * the invoice still generates with the org's default in that case, so there is
 * nothing for the rep to decide.
 *
 * Borrows xero-org-select.scss so the two selects line up as one row.
 */
export function XeroBrandingSelect({
  xeroConnectionId,
  value,
  onChange,
  disabled,
}) {
  // Only once an organisation is chosen: with no org the endpoint would answer
  // for the store's DEFAULT one, and offering another org's letterheads on the
  // sell flow (where the rep must pick an org first) would be actively wrong.
  const { data: themes = [], isLoading } = useXeroBrandingThemes(
    xeroConnectionId,
    !!xeroConnectionId
  );

  const defaultId = useMemo(
    () => themes.find((t) => t.is_default)?.id ?? themes[0]?.id ?? null,
    [themes]
  );

  if (!themes.length) {
    return null;
  }

  return (
    <div className="xero-org-select mb-4">
      <div className="strong-label mb-2">Branding:</div>
      <Select
        fullWidth
        label="Branding"
        options={themes.map((t) => ({
          value: t.id,
          label: t.is_default ? `${t.name} (default)` : t.name,
        }))}
        value={value ?? defaultId}
        disabled={disabled || isLoading}
        onChange={(v) => onChange(v || null)}
      />
    </div>
  );
}
